// Upload orchestration. End-to-end flow per Req 11:
//
//   1. Compute the bytes that will hit the wire. For at-rest
//      channels that's the plaintext file. For E2E channels it's
//      the AES-GCM ciphertext stream produced by `encryptFile` and
//      the wrapped file-key envelope.
//   2. SHA-256 the bytes (the server uses this for the at-rest
//      CSAM hash check and for cipher-stream integrity).
//   3. POST `/v1/uploads` with the metadata. Server returns a
//      presigned PUT URL.
//   4. PUT the bytes directly to Object_Store (S3 / R2 / MinIO).
//   5. POST `/v1/uploads/:id/finalize` with the SHA-256.
//   6. Server runs CSAM hash check (at-rest), encrypts under PSK
//      (at-rest), or just records SHA-256 (E2E), then returns the
//      finalized `AttachmentSummary` ready for a follow-on
//      `messages.send` call.
//
// Callers (the chatbox, the Files-pane drop zone, the message
// composer attach button) all share this single helper so the
// upload semantics are honored in one place.

import {
  encryptFile,
  decryptE2e,
  decryptFile,
  generateFileKey,
  importFileKey,
  parseE2eEnvelope,
  serializeE2eEnvelope,
  wrapFileKeyForRecipients,
  type E2eRecipient,
} from "@link/crypto";

import { api } from "./api";
import { readSessionToken } from "./http";
import { loadIdentity } from "./identity";
import { IMAGE_PREVIEW_MIMES } from "./message-presentation";
import { FILE_MAX_BYTES } from "@link/shared";

export interface UploadInput {
  file: File;
  channelXid: string;
  /** Optional folder hint surfaced in the Files pane. */
  folderPath?: string;
  /**
   * E2E key wrap recipients. When non-empty the file is encrypted
   * client-side and the wrapped FK is sent in `e2e_key_envelope_b64`.
   * For at-rest channels, leave undefined.
   */
  e2eRecipients?: ReadonlyArray<E2eRecipient>;
  /** Optional progress callback (0..1). */
  onProgress?: (fraction: number) => void;
}

export interface UploadResult {
  attachmentXid: string;
  filename: string;
  mime: string;
  byteSize: number;
}

/**
 * Run the full upload ceremony. Returns the finalized attachment
 * metadata so the caller can compose a message that references it.
 *
 * Throws on any step's failure — the caller should surface the
 * error in the UI so the user can retry.
 */
export async function uploadFile(input: UploadInput): Promise<UploadResult> {
  const { file, channelXid, folderPath, e2eRecipients, onProgress } = input;
  if (file.size === 0) throw new Error("This file is empty. Choose a file instead of a folder.");
  if (file.size > FILE_MAX_BYTES) throw new Error("Files must be 25 MB or smaller.");
  if (e2eRecipients !== undefined && e2eRecipients.length === 0) throw new Error("This conversation's encryption keys aren't ready. Try again in a moment.");

  // Decide encryption path.
  const isE2e = e2eRecipients !== undefined && e2eRecipients.length > 0;

  let bodyBytes: Uint8Array;
  let envelopeB64: string | undefined;
  let e2eContextId: string | undefined;

  if (isE2e) {
    // Generate FK, encrypt the file stream, collect the ciphertext
    // bytes, wrap the FK for every recipient, and serialize the
    // wrap envelope.
    const fk = await generateFileKey();
    const messageId = crypto.randomUUID();
    e2eContextId = messageId;
    const plaintextStream = file.stream();
    const cipherStream = encryptFile({
      fileKey: fk.key,
      stream: plaintextStream,
      channelId: channelXid,
      messageId,
    });
    bodyBytes = await collectStream(cipherStream);

    const envelope = await wrapFileKeyForRecipients({
      fileKey: fk,
      recipients: [...e2eRecipients],
      // Server records the wrap envelope alongside the upload
      // row; the messageId binding flows through the AAD baked
      // into the e2e envelope.
      channelId: channelXid,
      messageId,
    });
    envelopeB64 = bytesToB64(serializeE2eEnvelope(envelope));
    fk.raw.fill(0);
  } else {
    bodyBytes = new Uint8Array(await file.arrayBuffer());
  }

  // SHA-256 the wire bytes.
  const sha256 = await crypto.subtle.digest("SHA-256", bodyBytes);
  const sha256B64 = bytesToB64(new Uint8Array(sha256));

  // Step 3: presign.
  onProgress?.(0.1);
  const presigned = await api.uploads.presign({
    filename: file.name,
    mime: file.type || "application/octet-stream",
    byte_size: bodyBytes.byteLength,
    channel_xid: channelXid,
    folder_path: folderPath,
    e2e_key_envelope_b64: envelopeB64,
    e2e_context_id: e2eContextId,
  });

  // Step 4: PUT directly to Object_Store. Use XHR so we get
  // upload-progress events; fetch doesn't surface them.
  await putWithProgress(
    presigned.presigned_put_url,
    bodyBytes,
    file.type || "application/octet-stream",
    (fraction) => onProgress?.(0.1 + fraction * 0.8),
  );

  // Step 5: finalize.
  onProgress?.(0.95);
  const finalized = await api.uploads.finalize(presigned.upload_id, {
    sha256_b64: sha256B64,
  });

  onProgress?.(1);
  return {
    attachmentXid: finalized.attachment.xid,
    filename: finalized.attachment.filename,
    mime: finalized.attachment.mime_type,
    byteSize: finalized.attachment.byte_size,
  };
}

export async function getSelfFileRecipient(
  accountXid: string,
): Promise<E2eRecipient> {
  const identity = await loadIdentity(accountXid);
  if (identity === null) {
    throw new Error("Your local identity key is not available. Sign in again.");
  }
  return {
    accountIdHash: identity.publicKeyHash,
    identityPublicKey: identity.publicKeyRaw,
  };
}

/** Authenticated download; E2E files are decrypted on this device only. */
export async function downloadFileBlob(
  attachmentXid: string,
  accountXid: string,
  options: { signal?: AbortSignal; imagePreview?: boolean } = {},
): Promise<{ blob: Blob; filename: string }> {
  options.signal?.throwIfAborted();
  const metadata = await api.uploads.metadata(attachmentXid);
  options.signal?.throwIfAborted();
  if (options.imagePreview && (!IMAGE_PREVIEW_MIMES.has(metadata.mime_type) || metadata.byte_size > 26 * 1024 * 1024)) {
    throw new Error("This file isn't available as an image preview. You can still download it.");
  }
  const token = readSessionToken();
  const response = await fetch(api.uploads.downloadUrl(attachmentXid), {
    signal: options.signal,
    cache: "no-store",
    headers:
      token === null ? undefined : { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error(`Download failed (${response.status}).`);

  let blob: Blob;
  if (metadata.storage_kind === "atrest") {
    blob = await response.blob();
  } else {
    if (
      metadata.e2e_context_id === null ||
      metadata.e2e_key_envelope_b64 === null ||
      response.body === null
    ) {
      throw new Error("This encrypted file is missing its decryption metadata.");
    }
    const identity = await loadIdentity(accountXid);
    if (identity === null) throw new Error("Your local identity key is not available.");
    const envelope = parseE2eEnvelope(b64ToBytes(metadata.e2e_key_envelope_b64));
    const rawFileKey = await decryptE2e({
      envelope,
      recipientPrivateKey: identity.ecdhPrivateKey,
      recipientAccountIdHash: identity.publicKeyHash,
      channelId: metadata.channel_xid,
      messageId: metadata.e2e_context_id,
    });
    const fileKey = await importFileKey(rawFileKey);
    rawFileKey.fill(0);
    const plaintext = await collectStream(
      decryptFile({
        stream: response.body,
        fileKey,
        channelId: metadata.channel_xid,
        messageId: metadata.e2e_context_id,
      }),
    );
    blob = new Blob([plaintext], { type: metadata.mime_type });
  }
  options.signal?.throwIfAborted();
  // Trust the authorized metadata, not the download server's generic MIME.
  return { blob: new Blob([blob], { type: metadata.mime_type }), filename: metadata.filename };
}

export async function downloadAndSaveFile(attachmentXid: string, accountXid: string): Promise<void> {
  const { blob, filename } = await downloadFileBlob(attachmentXid, accountXid);
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
  } finally {
    window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
  }
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

async function collectStream(
  stream: ReadableStream<Uint8Array>,
): Promise<Uint8Array> {
  const reader = stream.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    if (value !== undefined) {
      chunks.push(value);
      total += value.byteLength;
    }
  }
  const out = new Uint8Array(total);
  let cursor = 0;
  for (const c of chunks) {
    out.set(c, cursor);
    cursor += c.byteLength;
  }
  return out;
}

function putWithProgress(
  url: string,
  body: Uint8Array,
  contentType: string,
  onProgress: (fraction: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const target = browserReachableUploadTarget(url);
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", target.url);
    xhr.setRequestHeader("Content-Type", contentType);
    if (target.requiresLinkAuth) {
      const token = readSessionToken();
      if (token !== null && token.length > 0) {
        xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      }
    }
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        onProgress(e.loaded / e.total);
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`object-store PUT failed: ${xhr.status}`));
    };
    xhr.onerror = () =>
      reject(
        new Error(
          `Could not reach file storage at ${new URL(target.url).origin}.`,
        ),
      );
    xhr.send(body);
  });
}

/**
 * Link's authenticated content endpoint must stay on the SPA's origin so the
 * Vite/nginx proxy can carry it to the API without CORS. A real presigned
 * object-store URL is already authenticated by its query string and must
 * never receive Link's bearer token.
 */
function browserReachableUploadTarget(value: string): {
  url: string;
  requiresLinkAuth: boolean;
} {
  const parsed = new URL(value, window.location.origin);
  const isLinkContentEndpoint =
    parsed.pathname.startsWith("/v1/files/uploads/") &&
    parsed.pathname.endsWith("/content");
  if (isLinkContentEndpoint) {
    return {
      url: new URL(`${parsed.pathname}${parsed.search}`, window.location.origin).toString(),
      requiresLinkAuth: true,
    };
  }
  if (
    import.meta.env.DEV &&
    (parsed.hostname === "minio" || parsed.hostname === "link-minio")
  ) {
    parsed.hostname = window.location.hostname || "127.0.0.1";
    parsed.port = "9000";
  }
  return { url: parsed.toString(), requiresLinkAuth: false };
}

function bytesToB64(bytes: Uint8Array): string {
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin);
}

function b64ToBytes(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}
